// crewbit-admin/src/components/money/InvoiceActionRow.tsx
//
// Spec: §InvoiceDetail.7.3. Renders InvoiceAction[] inline + overflow.
// Up to 5 inline (sorted by tone priority); 6+ tucked into "More" menu.

import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { palette, urgency, spacing, typography, transition } from '../../styles/tokens';
import type { ActionId, ActionTone, InvoiceAction } from '../../lib/invoiceActions';

const INLINE_LIMIT = 5;
const TONE_PRIORITY: Record<ActionTone, number> = {
  primary_red: 0,
  primary_amber: 1,
  primary: 2,
  secondary: 3,
  overflow: 4,
};

function toneStyle(tone: ActionTone, disabled: boolean): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 999,
    fontSize: typography.size.caption,
    fontWeight: typography.weight.semibold,
    fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    border: '1px solid transparent',
    transition: `background ${transition.fast}`,
    whiteSpace: 'nowrap',
  };
  switch (tone) {
    case 'primary':
      return { ...base, background: palette.accent, color: palette.white };
    case 'primary_amber':
      return { ...base, background: urgency.warning, color: palette.white };
    case 'primary_red':
      return { ...base, background: palette.red, color: palette.white };
    case 'secondary':
      return {
        ...base,
        background: palette.white,
        color: palette.accent,
        border: `1px solid ${palette.accentSoft}`,
      };
    case 'overflow':
      return {
        ...base,
        background: 'transparent',
        color: palette.g700,
        border: 'none',
      };
  }
}

interface Props {
  actions: InvoiceAction[];
  onAction: (id: ActionId) => void;
}

export function InvoiceActionRow({ actions, onAction }: Props) {
  const sorted = [...actions].sort((a, b) => TONE_PRIORITY[a.tone] - TONE_PRIORITY[b.tone]);
  const inline = sorted.slice(0, INLINE_LIMIT);
  const overflow = sorted.slice(INLINE_LIMIT);
  const explicitOverflow = inline.filter((a) => a.tone === 'overflow');
  const inlineVisible = inline.filter((a) => a.tone !== 'overflow');
  const overflowAll = [...explicitOverflow, ...overflow];

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  if (sorted.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
      {inlineVisible.map((a) => (
        <button
          key={a.id}
          onClick={() => !a.disabled && onAction(a.id)}
          disabled={!!a.disabled}
          title={a.disabled?.reason}
          style={toneStyle(a.tone, !!a.disabled)}
        >
          {a.label}
        </button>
      ))}

      {overflowAll.length > 0 && (
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="More actions"
            style={{
              ...toneStyle('secondary', false),
              padding: '8px 12px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <MoreHorizontal size={14} />
            More
          </button>
          {menuOpen && (
            <div
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                background: palette.white, border: `1px solid ${palette.g200}`,
                borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                padding: 6, minWidth: 200, zIndex: 60,
              }}
            >
              {overflowAll.map((a) => (
                <button
                  key={a.id}
                  onClick={() => {
                    if (a.disabled) return;
                    setMenuOpen(false);
                    onAction(a.id);
                  }}
                  disabled={!!a.disabled}
                  title={a.disabled?.reason}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 12px', borderRadius: 8, border: 'none',
                    background: 'transparent', color: palette.g700,
                    fontSize: typography.size.caption, fontFamily: 'inherit',
                    cursor: a.disabled ? 'not-allowed' : 'pointer',
                    opacity: a.disabled ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => { if (!a.disabled) e.currentTarget.style.background = palette.g50; }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
